import { useCallback } from "react";
import { WorksheetItemInput } from "DDD/action-objects/WorksheetItemUpdate";
import { ProposalProduct, WorksheetPricingMode } from "generated/graphql";
import { dollarsToCents, parseDecimalInput } from "./worksheetFormValues";

export const parseCurrencyInput = (value: string) => {
  const num = parseFloat(value.replace(/[^0-9.-]/g, ""));

  return isNaN(num) ? "" : String(num);
};

type InlineUpdate = (
  patch: Partial<WorksheetItemInput>,
  options?: { captureBaseline?: boolean }
) => Promise<boolean>;

const toFixDollars = (cents: number) => (cents / 100).toFixed(2);

export default function useInlineFinancialEdits(
  handleInlineUpdate: InlineUpdate,
  product: ProposalProduct
) {
  const costCents = Number(product.costTotal ?? 0);

  const saveRate = useCallback(
    async (value: string) => {
      await handleInlineUpdate(
        {
          sellingRate: value,
          pricingMode: WorksheetPricingMode.RATE,
          pricingOverrideField: "rate",
        },
        { captureBaseline: true }
      );

      return value;
    },
    [handleInlineUpdate]
  );

  const savePercentage =
    (field: "overheadPercentage" | "indirectPercentage") =>
    async (value: string): Promise<string> => {
      await handleInlineUpdate({ [field]: value });

      return value;
    };

  const saveProfit = useCallback(
    async (value: string) => {
      await handleInlineUpdate(
        {
          pricingMode: WorksheetPricingMode.FIXED,
          fixedTotal: toFixDollars(costCents + dollarsToCents(value)),
          pricingOverrideField: "profit",
        },
        { captureBaseline: true }
      );

      return value;
    },
    [costCents, handleInlineUpdate]
  );

  const saveMargin = useCallback(
    async (value: string) => {
      const margin = parseDecimalInput(value);

      if (costCents === 0 || margin >= 100) {
        return value;
      }

      const total = Math.round(costCents / (1 - margin / 100));

      await handleInlineUpdate(
        {
          pricingMode: WorksheetPricingMode.FIXED,
          fixedTotal: toFixDollars(total),
          pricingOverrideField: "margin",
        },
        { captureBaseline: true }
      );

      return value;
    },
    [costCents, handleInlineUpdate]
  );

  const saveDiscount = useCallback(
    async (value: string) => {
      await handleInlineUpdate({ discount: value });

      return value;
    },
    [handleInlineUpdate]
  );

  const saveTotal = useCallback(
    async (value: string) => {
      await handleInlineUpdate(
        {
          pricingMode: WorksheetPricingMode.FIXED,
          fixedTotal: value,
          pricingOverrideField: "total",
        },
        { captureBaseline: true }
      );

      return value;
    },
    [handleInlineUpdate]
  );

  return { costCents, saveRate, savePercentage, saveProfit, saveMargin, saveDiscount, saveTotal };
}
